import React, { useState } from 'react';
import methods from '../../data/methodsTransformed';
import aiLinkLogo from './logo.png';

const dataTypes = [
  { name: 'Array', icon: 'fas fa-table', color: 'from-blue-500 to-cyan-600' },
  { name: 'Boolean', icon: 'fas fa-check-circle', color: 'from-green-500 to-emerald-600' },
  { name: 'Date', icon: 'fas fa-calendar-alt', color: 'from-purple-500 to-violet-600' },
  { name: 'Number', icon: 'fas fa-hashtag', color: 'from-red-500 to-rose-600' },
  { name: 'Object', icon: 'fas fa-cube', color: 'from-amber-500 to-orange-600' },
  { name: 'String', icon: 'fas fa-font', color: 'from-indigo-500 to-blue-600' }
];
const typeToColor = dataTypes.reduce((acc, type) => {
  acc[type.name] = type.color;
  return acc;
}, {});

export default function SideBar({ selectedType, setSelectedType, isListVisible, setIsListVisible }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Filter methods based on selected type
  const filteredMethods = selectedType 
    ? methods.filter(method => method.datatype === selectedType)
    : methods;

  const handleMethodClick = (methodName) => {
    const elementId = methodName;
    const element = document.getElementById(elementId);
    if (!element) {
      console.warn(`Element with id "${elementId}" not found. Make sure the method cards have the correct IDs.`);
      return;
    }

    // Scroll to the element
    element.scrollIntoView({ 
      behavior: 'smooth',
      block: 'center'
    });

    // Add highlight effect
    element.classList.add('highlight-card');
    setTimeout(() => {
      element.classList.remove('highlight-card');
    }, 2000);

    // Close mobile menu after selection on mobile
    setIsMobileMenuOpen(false);
  };

  // Mobile menu button component
  const MobileMenuButton = () => (
    <button
      onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      className="lg:hidden fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
    >
      <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
    </button>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className={`
        hidden lg:flex fixed left-0 top-0 h-screen bg-gray-900 text-white shadow-xl z-50 
        transition-[width] duration-300 ease-in-out
        ${isListVisible ? 'w-[450px]' : 'w-64'}
      `}>
        {/* Left Column - Type Filters */}
        <div className="w-64 min-w-[16rem] border-r border-gray-800 relative">
          {/* Logo/Header */}
          <div className="p-8 border-b border-gray-800 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            {/* Logos Container */}
            <div className="flex items-center justify-center gap-6 mb-6">
      
              <img 
                src="https://n8n.io/favicon.ico" 
                alt="n8n Logo" 
                className="h-24 w-24"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/48x48/374151/ffffff?text=n8n';
                }}
              />
            </div>
            
            {/* Title and Subtitle */}
            <div className="space-y-3">
              <div className="relative">
                <h1 className="text-2xl font-bold text-center bg-gradient-to-r from-blue-400 via-purple-400 to-purple-500 bg-clip-text text-transparent">
                  Javascript en n8n
                </h1>
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-[2px] bg-gradient-to-r from-blue-500/0 via-blue-500 to-purple-500/0"></div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4 h-[calc(100vh-200px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm uppercase tracking-wider text-gray-400">Data Types</h2>
                <button
                  onClick={() => setIsListVisible(!isListVisible)}
                  className={`
                    group flex items-center gap-2 px-3 py-1.5 rounded-lg
                    bg-gray-800/50 hover:bg-gray-800
                    transition-all duration-300 ease-in-out
                    border border-gray-700 hover:border-gray-600
                    text-gray-400 hover:text-white
                  `}
                  title={isListVisible ? "Ocultar lista" : "Mostrar lista"}
                >
                  <span className="text-xs tracking-wider">
                    {isListVisible ? "Ocultar" : "Mostrar"}
                  </span>
                  <i className={`fas fa-chevron-right text-xs transition-transform duration-300 ${isListVisible ? 'rotate-180' : ''}`}></i>
                </button>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedType(null)}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-all duration-200 ${
                    selectedType === null
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-400 hover:bg-gray-800/50'
                  }`}
                >
                  All Types ({methods.length})
                </button>
                {dataTypes.map((type) => (
                  <button
                    key={type.name}
                    onClick={() => setSelectedType(type.name)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-3 ${
                      selectedType === type.name
                        ? 'bg-gradient-to-r ' + type.color + ' text-white'
                        : 'text-gray-400 hover:bg-gray-800/50'
                    }`}
                  >
                    <i className={type.icon}></i>
                    {type.name} ({methods.filter(m => m.datatype === type.name).length})
                  </button>
                ))}
              </div>
            </div>

            {/* Documentation Link */}
            <div className="mt-8">
              <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-4">Documentación</h2>
              <div className="space-y-2">
                <a
                  href="https://docs.n8n.io/code/builtin/data-transformation-functions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2 text-gray-400 hover:bg-gray-800/50 rounded-lg transition-all duration-200"
                >
                  n8n Docs
                </a>
              </div>
            </div>
          </nav>
        </div>

        {/* Right Column - Methods List */}
        <div className={`
          relative overflow-hidden transition-[width,opacity,transform] duration-300 ease-in-out
          ${isListVisible ? 'w-[calc(450px-16rem)] opacity-100' : 'w-0 opacity-0'}
        `}>
          <div className={`
            absolute top-0 left-0 min-w-max h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800
            transition-transform duration-300 ease-in-out
            ${isListVisible ? 'translate-x-0' : '-translate-x-4'}
          `}>
            <div className="p-6">{selectedType}
              <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-4 whitespace-nowrap">
                {selectedType ? `Métodos ${selectedType}` : 'Todos los métodos'} ({filteredMethods.length})
              </h2>
              <div className="space-y-1">
                {filteredMethods.map((method,index) => (
                  <div
                    key={index}
                    className="px-3 py-1.5 rounded-md hover:bg-gray-800/50 transition-all duration-200 cursor-pointer w-fit whitespace-nowrap group"
                    onClick={() => handleMethodClick(method.name+method.datatype)}
                  >
                    <div className="flex items-center gap-2">
                      <div  className="font-medium text-gray-300 group-hover:text-white transition-colors duration-200">
                        {method.name}
                      </div>
                      <div className={`px-2 py-0.5 rounded-full bg-gradient-to-r ${typeToColor[method.datatype] } text-400 text-xs font-medium`}>
                        {method.datatype}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Button */}
      <MobileMenuButton />

      {/* Mobile Menu */}
      <div className={`
        lg:hidden fixed inset-0 bg-gray-900 z-40 transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-y-0' : 'translate-y-full'}
      `}>
        {/* Mobile Header */}
        <div className="p-6 bg-gradient-to-r from-gray-900 to-gray-800 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src={aiLinkLogo}
                alt="AiLink Logo" 
                className="h-10 w-10"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/40x40/374151/ffffff?text=AL';
                }}
              />
              <h1 className="text-xl font-bold text-white">Javascript en n8n</h1>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="p-4 h-[calc(100vh-88px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          {/* Type Filters */}
          <div className="mb-6">
            <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-3">Data Types</h2>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setSelectedType(null)}
                className={`text-center px-3 py-2 rounded-lg transition-all duration-200 ${
                  selectedType === null
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-400 bg-gray-800/50'
                }`}
              >
                All ({methods.length})
              </button>
              {dataTypes.map((type) => (
                <button
                  key={type.name}
                  onClick={() => setSelectedType(type.name)}
                  className={`text-center px-3 py-2 rounded-lg transition-all duration-200 ${
                    selectedType === type.name
                      ? 'bg-gradient-to-r ' + type.color + ' text-white'
                      : 'text-gray-400 bg-gray-800/50'
                  }`}
                >
                  <i className={`${type.icon} mr-2`}></i>
                  {type.name}
                </button>
              ))}
            </div>
          </div>

          {/* Methods List */}
          <div className="space-y-4">
            <h2 className="text-sm uppercase tracking-wider text-gray-400">
              {selectedType ? `${selectedType} Methods` : 'All Methods'} ({filteredMethods.length})
            </h2>
            <div className="grid grid-cols-1 gap-2">
              {filteredMethods.map((method, index) => (
                <button
                  key={index}
                  onClick={() => handleMethodClick(method.name+method.datatype)}
                  className="w-full text-left px-4 py-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-300">{method.name}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${typeToColor[method.datatype]} text-white`}>
                      {method.datatype}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Documentation Link */}
          <div className="mt-8 border-t border-gray-800 pt-6">
            <a
              href="https://docs.n8n.io/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 rounded-lg text-gray-300 hover:text-white transition-colors duration-200"
            >
              <i className="fas fa-external-link-alt"></i>
              n8n Documentation
            </a>
          </div>
        </div>
      </div>
    </>
  );
} 